import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstanceNew } from 'src/utils/axios';
import { isIncomingTxFresh } from 'src/constants/transactionStatus';

const DEFAULT_PAGE_SIZE = 10;

export const fetchAdminTransactions = createAsyncThunk(
  'adminTransactions/fetchAdminTransactions',
  async ({ queryParams }, { rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.get('/admin/transactions', {
        params: Object.fromEntries(queryParams.entries()),
      });

      return {
        list: res.data.transactions || [],
        meta: res.data.meta || {},
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const markPayoutQueued = createAsyncThunk(
  'adminTransactions/markPayoutQueued',
  async ({ id, payoutTxHash }, { rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.post(`/admin/transaction/${id}/payout-queued`, {
        payoutTxHash,
      });

      const ok = Boolean(res.data?.success || res.data?.ok);

      if (!ok) {
        throw new Error(res.data?.message || 'Failed to mark payout queued.');
      }

      return res.data?.transaction || res.data?.tx || null;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Frontend-broadcast model: the payout is signed and broadcast from the
// connected wallet in the component. This thunk hits the RECORD-ONLY backend
// route, which saves the txHash to txHashOut, sets payout_queued, and kicks the
// confirmation watcher. The txHash MUST be sent in the request body — the route
// rejects with INVALID_TX_HASH without it.
export const recordCryptoPayoutHash = createAsyncThunk(
  'adminTransactions/recordCryptoPayoutHash',
  async ({ id, txHash }, { rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.post(`/admin/transaction/${id}/payout/crypto`, {
        txHash,
      });

      const ok = Boolean(res.data?.success || res.data?.ok);

      if (!ok) {
        throw new Error(res.data?.message || 'Failed to record crypto payout.');
      }

      return res.data?.transaction || res.data?.tx || null;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const speedUpPayout = createAsyncThunk(
  'adminTransactions/speedUpPayout',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.post(`/admin/transaction/${id}/payout/speed-up`);

      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to speed up payout.');
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

function ensureMeta(state) {
  if (!state.meta) state.meta = {};
  state.meta.pageSize = Number(state.meta.pageSize) || DEFAULT_PAGE_SIZE;
  state.meta.total = Number(state.meta.total) || 0;
  state.meta.statusCount = state.meta.statusCount || {};
}

function findTxIndex(items, id) {
  if (!Array.isArray(items)) return -1;
  return items.findIndex((tx) => String(tx._id) === String(id));
}

function replaceVisibleTx(state, updatedTx) {
  if (!updatedTx?._id) return;

  const idx = findTxIndex(state.items, updatedTx._id);

  if (idx === -1) return;
  if (!isIncomingTxFresh(state.items[idx], updatedTx)) return;

  state.items[idx] = {
    ...state.items[idx],
    ...updatedTx,
  };
}

function upsertVisibleTx(state, tx) {
  if (!tx?._id) return;

  if (!Array.isArray(state.items)) state.items = [];

  const idx = findTxIndex(state.items, tx._id);

  if (idx === -1) {
    state.items.unshift(tx);
    ensureMeta(state);
    state.meta.total += 1;
    return;
  }

  if (!isIncomingTxFresh(state.items[idx], tx)) return;

  state.items[idx] = {
    ...state.items[idx],
    ...tx,
  };
}

const initialState = {
  items: [],
  meta: {},
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  markPayoutQueued: { loading: false, error: null },
  recordCryptoPayoutHash: { loading: false, error: null },
  speedUpPayout: { loading: false, error: null, action: null, message: null },
};

const adminTransactionsSlice = createSlice({
  name: 'adminTransactions',
  initialState,
  reducers: {
    clearAdminTransactions(state) {
      state.items = [];
      state.meta = {};
      state.error = null;
      state.isLoading = false;
      state.lastFetchedAt = null;
      state.markPayoutQueued = { loading: false, error: null };
      state.recordCryptoPayoutHash = { loading: false, error: null };
      state.speedUpPayout = { loading: false, error: null, action: null, message: null };
    },
    prependAdminTransaction(state, action) {
      upsertVisibleTx(state, action.payload);
    },
    mergeAdminTransaction(state, action) {
      replaceVisibleTx(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.list || [];
        state.meta = action.payload.meta || {};
        ensureMeta(state);
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchAdminTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
        state.items = [];
        state.meta = {};
      })
      .addCase(markPayoutQueued.pending, (state) => {
        state.markPayoutQueued.loading = true;
        state.markPayoutQueued.error = null;
      })
      .addCase(markPayoutQueued.fulfilled, (state, action) => {
        state.markPayoutQueued.loading = false;
        replaceVisibleTx(state, action.payload);
      })
      .addCase(markPayoutQueued.rejected, (state, action) => {
        state.markPayoutQueued.loading = false;
        state.markPayoutQueued.error = action.payload || action.error.message;
      })
      .addCase(recordCryptoPayoutHash.pending, (state) => {
        state.recordCryptoPayoutHash.loading = true;
        state.recordCryptoPayoutHash.error = null;
      })
      .addCase(recordCryptoPayoutHash.fulfilled, (state, action) => {
        state.recordCryptoPayoutHash.loading = false;
        replaceVisibleTx(state, action.payload);
      })
      .addCase(recordCryptoPayoutHash.rejected, (state, action) => {
        state.recordCryptoPayoutHash.loading = false;
        state.recordCryptoPayoutHash.error = action.payload || action.error.message;
      })
      .addCase(speedUpPayout.pending, (state) => {
        state.speedUpPayout.loading = true;
        state.speedUpPayout.error = null;
        state.speedUpPayout.action = null;
        state.speedUpPayout.message = null;
      })
      .addCase(speedUpPayout.fulfilled, (state, action) => {
        state.speedUpPayout.loading = false;
        state.speedUpPayout.action = action.payload.action || null;
        state.speedUpPayout.message = action.payload.message || null;

        const tx =
          action.payload.transaction ||
          action.payload.tx ||
          action.payload.reconciliation?.transaction ||
          action.payload.speedUp?.transaction ||
          null;

        if (tx) replaceVisibleTx(state, tx);
      })
      .addCase(speedUpPayout.rejected, (state, action) => {
        state.speedUpPayout.loading = false;
        state.speedUpPayout.error = action.payload || action.error.message;
      });
  },
});

export const { clearAdminTransactions, prependAdminTransaction, mergeAdminTransaction } =
  adminTransactionsSlice.actions;

export default adminTransactionsSlice.reducer;
